import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Megaphone, Plus, Pencil, Trash2, Eye, EyeOff, Loader2, Info, AlertTriangle, CheckCircle, XCircle, Wrench } from 'lucide-react';
import { adminAPI } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { toast } from 'sonner';

const ANNOUNCEMENT_TYPES = [
    { value: 'info', label: 'Informare', icon: Info, color: 'bg-blue-100 text-blue-700' },
    { value: 'warning', label: 'Avertizare', icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-700' },
    { value: 'success', label: 'Succes', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
    { value: 'error', label: 'Important', icon: XCircle, color: 'bg-red-100 text-red-700' },
];

const AdminAnnouncementsTab = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [announcementToDelete, setAnnouncementToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // Maintenance mode state
    const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [maintenanceLoading, setMaintenanceLoading] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        type: 'info',
        is_visible: true,
        expires_at: ''
    });
    
    useEffect(() => {
        fetchAnnouncements();
        fetchMaintenanceStatus();
    }, []);
    
    const fetchMaintenanceStatus = async () => {
        try {
            const response = await adminAPI.getMaintenanceStatus();
            setMaintenanceEnabled(response.data?.enabled || false);
            setMaintenanceMessage(response.data?.message || '');
        } catch (error) {
            console.error('Error fetching maintenance status:', error);
        }
    };
    
    const handleMaintenanceToggle = async (enabled) => {
        setMaintenanceLoading(true);
        try {
            await adminAPI.setMaintenanceMode(enabled, maintenanceMessage || 'Site-ul este în mentenanță. Revenim în curând.');
            setMaintenanceEnabled(enabled);
            toast.success(enabled ? 'Mod mentenanță activat' : 'Mod mentenanță dezactivat');
        } catch (error) {
            toast.error('Eroare la schimbarea modului de mentenanță');
        } finally {
            setMaintenanceLoading(false);
        }
    };
    
    const handleMaintenanceMessageSave = async () => {
        if (!maintenanceEnabled) return;
        setMaintenanceLoading(true);
        try {
            await adminAPI.setMaintenanceMode(maintenanceEnabled, maintenanceMessage);
            toast.success('Mesaj actualizat');
        } catch (error) {
            toast.error('Eroare la salvarea mesajului');
        } finally {
            setMaintenanceLoading(false);
        }
    };
    
    const fetchAnnouncements = async () => {
        try {
            const response = await adminAPI.getAnnouncements();
            setAnnouncements(response.data || []);
        } catch (error) {
            toast.error('Eroare la încărcarea anunțurilor');
        } finally {
            setLoading(false);
        }
    };
    
    const openCreateDialog = () => {
        setEditingAnnouncement(null);
        setFormData({
            title: '',
            content: '',
            type: 'info',
            is_visible: true,
            expires_at: ''
        });
        setDialogOpen(true);
    };
    
    const openEditDialog = (announcement) => {
        setEditingAnnouncement(announcement);
        setFormData({
            title: announcement.title,
            content: announcement.content,
            type: announcement.type || 'info',
            is_visible: announcement.is_visible,
            expires_at: announcement.expires_at || ''
        });
        setDialogOpen(true);
    };
    
    const handleSave = async () => {
        if (!formData.title.trim() || !formData.content.trim()) {
            toast.error('Completează titlul și conținutul');
            return;
        }
        
        setSaving(true);
        try {
            if (editingAnnouncement) {
                await adminAPI.updateAnnouncement(editingAnnouncement.announcement_id, formData);
                toast.success('Anunț actualizat cu succes');
            } else {
                await adminAPI.createAnnouncement(formData);
                toast.success('Anunț creat cu succes');
            }
            setDialogOpen(false);
            fetchAnnouncements();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la salvarea anunțului');
        } finally {
            setSaving(false);
        }
    };
    
    const handleToggleVisibility = async (announcement) => {
        try {
            await adminAPI.updateAnnouncement(announcement.announcement_id, {
                is_visible: !announcement.is_visible
            });
            toast.success(announcement.is_visible ? 'Anunț ascuns' : 'Anunț vizibil');
            fetchAnnouncements();
        } catch (error) {
            toast.error('Eroare la actualizarea vizibilității');
        }
    };
    
    const openDeleteDialog = (announcement) => {
        setAnnouncementToDelete(announcement);
        setDeleteDialogOpen(true);
    };
    
    const handleDelete = async () => {
        if (!announcementToDelete) return;
        
        setDeleting(true);
        try {
            await adminAPI.deleteAnnouncement(announcementToDelete.announcement_id);
            toast.success('Anunț șters cu succes');
            setDeleteDialogOpen(false);
            fetchAnnouncements();
        } catch (error) {
            toast.error('Eroare la ștergerea anunțului');
        } finally {
            setDeleting(false);
        }
    };
    
    const getTypeConfig = (type) => {
        return ANNOUNCEMENT_TYPES.find(t => t.value === type) || ANNOUNCEMENT_TYPES[0];
    };
    
    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
            </div>
        );
    }
    
    return (
        <div className="space-y-6">
            {/* Maintenance Mode Card */}
            <Card className="border-orange-200 bg-orange-50/50">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-orange-600" />
                        Mod Mentenanță
                    </CardTitle>
                    <CardDescription>
                        Când este activ, utilizatorii nu pot efectua acțiuni pe site și vor vedea un mesaj de mentenanță.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                        <div>
                            <Label className="font-semibold text-base">Activează modul mentenanță</Label>
                            <p className="text-sm text-muted-foreground">
                                {maintenanceEnabled 
                                    ? '⚠️ Site-ul este în mentenanță - utilizatorii nu pot efectua acțiuni'
                                    : 'Site-ul funcționează normal'}
                            </p>
                        </div>
                        <Switch
                            checked={maintenanceEnabled}
                            onCheckedChange={handleMaintenanceToggle}
                            disabled={maintenanceLoading}
                            data-testid="maintenance-toggle"
                        />
                    </div>
                    
                    {maintenanceEnabled && (
                        <div className="space-y-2">
                            <Label htmlFor="maintenance-message">Mesaj afișat utilizatorilor</Label>
                            <div className="flex gap-2">
                                <Input
                                    id="maintenance-message"
                                    value={maintenanceMessage}
                                    onChange={(e) => setMaintenanceMessage(e.target.value)}
                                    placeholder="Site-ul este în mentenanță. Revenim în curând."
                                    className="flex-1"
                                />
                                <Button 
                                    onClick={handleMaintenanceMessageSave}
                                    disabled={maintenanceLoading}
                                    variant="outline"
                                >
                                    {maintenanceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvează'}
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                        <Megaphone className="w-5 h-5" />
                        Anunțuri Frontpage
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Gestionează anunțurile afișate pe pagina principală pentru utilizatorii autentificați
                    </p>
                </div>
                <Button onClick={openCreateDialog} data-testid="create-announcement-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Anunț nou
                </Button>
            </div>
            
            {/* Announcements List */}
            {announcements.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Megaphone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <p className="text-muted-foreground">Nu există anunțuri</p>
                        <p className="text-sm text-muted-foreground mt-1">
                            Creează primul anunț pentru a-l afișa pe frontpage
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {announcements.map((announcement) => {
                        const typeConfig = getTypeConfig(announcement.type);
                        const TypeIcon = typeConfig.icon;
                        
                        return (
                            <Card 
                                key={announcement.announcement_id}
                                className={`transition-all ${!announcement.is_visible ? 'opacity-60' : ''}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Badge className={typeConfig.color}>
                                                    <TypeIcon className="w-3 h-3 mr-1" />
                                                    {typeConfig.label}
                                                </Badge>
                                                {!announcement.is_visible && (
                                                    <Badge variant="outline" className="text-muted-foreground">
                                                        <EyeOff className="w-3 h-3 mr-1" />
                                                        Ascuns
                                                    </Badge>
                                                )}
                                                {announcement.expires_at && (
                                                    <Badge variant="outline" className="text-orange-600">
                                                        Expiră: {formatDateTime(announcement.expires_at)}
                                                    </Badge>
                                                )}
                                            </div>
                                            <h3 className="font-semibold text-lg">{announcement.title}</h3>
                                            <p className="text-muted-foreground mt-1 whitespace-pre-wrap">
                                                {announcement.content}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-3">
                                                Creat de {announcement.created_by_name} • {formatDateTime(announcement.created_at)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleToggleVisibility(announcement)}
                                                title={announcement.is_visible ? 'Ascunde anunțul' : 'Afișează anunțul'}
                                            >
                                                {announcement.is_visible ? (
                                                    <Eye className="w-4 h-4" />
                                                ) : (
                                                    <EyeOff className="w-4 h-4" />
                                                )}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => openEditDialog(announcement)}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                onClick={() => openDeleteDialog(announcement)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
            
            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Megaphone className="w-5 h-5" />
                            {editingAnnouncement ? 'Editează anunțul' : 'Anunț nou'}
                        </DialogTitle>
                        <DialogDescription>
                            Acest anunț va fi afișat pe pagina principală pentru utilizatorii autentificați.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="title">Titlu</Label>
                            <Input
                                id="title"
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Titlul anunțului"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="content">Conținut</Label>
                            <Textarea
                                id="content"
                                value={formData.content}
                                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                                placeholder="Scrie conținutul anunțului..."
                                rows={4}
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="type">Tip</Label>
                                <Select
                                    value={formData.type}
                                    onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                                >
                                    <SelectTrigger id="type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ANNOUNCEMENT_TYPES.map((type) => (
                                            <SelectItem key={type.value} value={type.value}>
                                                <div className="flex items-center gap-2">
                                                    <type.icon className="w-4 h-4" />
                                                    {type.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="expires">Expiră la (opțional)</Label>
                                <Input
                                    id="expires"
                                    type="datetime-local"
                                    value={formData.expires_at ? formData.expires_at.slice(0, 16) : ''}
                                    onChange={(e) => setFormData(prev => ({ 
                                        ...prev, 
                                        expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''
                                    }))}
                                />
                            </div>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label htmlFor="visible" className="font-medium">Vizibil pe frontpage</Label>
                                <p className="text-xs text-muted-foreground">
                                    Anunțul va fi afișat imediat după creare
                                </p>
                            </div>
                            <Switch
                                id="visible"
                                checked={formData.is_visible}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_visible: checked }))}
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Anulează
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingAnnouncement ? 'Salvează' : 'Creează'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 className="w-5 h-5" />
                            Șterge anunțul
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Ești sigur că vrei să ștergi anunțul "<strong>{announcementToDelete?.title}</strong>"? 
                            Această acțiune nu poate fi anulată.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Anulează</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Șterge
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminAnnouncementsTab;
